import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
import { assetVariantUrl, snapVariantWidth } from '../_shared/assetVariant.js';
import { productWarmKey, warmVariantImages } from '../_shared/imageWarm.js';
import { useProductState } from '../_shared/productContext.js';
import { OPTION_SEP } from '../_shared/unitVariants.js';
/**
 * Shrine `variant-picker` — config-by-config, with one deliberate divergence: Shrine exposes a free-text
 * `picker_types` CSV (a render type per option, e.g. "swatches, pills, dropdown"); we keep our cleaner
 * single `variantStyle` select (auto/swatch/pills/dropdown), applied as the DEFAULT for EVERY option row.
 *
 * MULTI-OPTION products (2+ options, e.g. Color × Size) render ONE ROW PER OPTION (the top-tier PDP
 * anatomy: a Color swatch row, then a Size pills row) instead of a flat list of full-variant combos.
 * Selection stays ONE value per option; the resolved variant is the one whose canonical label (option
 * values joined " / ", see renderer/context.ts#StorefrontVariant.label) matches the combination — the
 * shared `variantId` remains the single source of truth, so price/sku/cod-form/sticky-atc are untouched.
 * Single-option products (and any variant set whose labels don't parse against the options) keep the
 * EXACT historical flat render (back-compat).
 *
 * Reproduced Shrine groups: General (skip_unavailable), Labels (custom_labels + size/font/color +
 * label lines + alignment), Color swatches (swatches_size + custom-color source/list), Step indicator
 * (show + size + bg/text color + corner radius), Dropdowns (full_width_dropdowns), + Margins(15,15).
 *
 * ID MAPPING (Shrine id → our id, kept stable so the validated preset/tests don't churn):
 *   - Shrine `picker_types` (free-text CSV per option) → our single `variantStyle` select.
 *   - Shrine `swatches_custom_colors` → `swatchesCustomColors`; `swatches_custom_colors_list` →
 *     `swatchesCustomColorsList`; `full_width_dropdowns` → `fullWidthDropdowns`; `show_label_lines` →
 *     `showLabelLines`; `labels_alignment` → `labelsAlignment`; `labels_line_color` → `labelsLineColor`;
 *     `labels_line_custom_color` → `labelsLineCustomColor`; `labels_line_thickness` → `labelsLineThickness`;
 *     `step_indicator_bg_color` → `stepIndicatorBgColor`; `step_indicator_text_color` →
 *     `stepIndicatorTextColor`; `step_indicator_corner_radius` → `stepIndicatorCornerRadius`.
 *
 * DEFERRED (Shrine has them; large sub-project / Shopify-platform, noted so the founder can audit):
 *   - the full Quantity Breaks "BUNDLE & SAVE" group (31 breaks_* settings: style/headline/prices_layout/
 *     badges/benefits/captions/price_texts/images/… — a bundle-pricing render mode bound to per-quantity
 *     discounts we don't model yet; documented large sub-project — we keep the classic stepper).
 *   - the Sizing-chart `append` setting (positions a nested Shopify `product_sizing-chart` block we don't ship).
 *   - swatches_custom_colors options `image_alt`/`predefined`/`metafield` resolve from Shopify variant
 *     image alt text / theme settings / category metafields — platform-shaped; we wire `custom` (CSS list)
 *     + `disabled` now, the Shopify-sourced options remain selectable but fall back to variant swatch.
 * NO responsive: Shrine has no desktop/mobile variants for this block (responsive lives in the partial).
 */
export const variantPickerBlockSchema = {
    type: 'variant-picker',
    name: 'Variant picker',
    max: 1,
    fields: [
        visibilityField,
        // ── General ─────────────────────────────────────────────────────────────
        {
            id: 'variantStyle',
            type: 'select',
            label: 'Variant style',
            group: 'General',
            default: 'auto',
            options: [
                { value: 'auto', label: 'Auto (swatch if colour, else pills)' },
                { value: 'swatch', label: 'Swatches' },
                { value: 'pills', label: 'Pills' },
                { value: 'dropdown', label: 'Dropdown' },
            ],
        },
        {
            // How NON-swatch values render: 'pills' (compact rounded chips — the historical default) or
            // 'buttons' (equal-width squared buttons that FILL the row, selected = accent border + tint —
            // the top-tier PDP "Standard | Mini" segmented size row). Applies to every pill-rendered value
            // (flat AND multi-option paths); swatch/dropdown rows are untouched.
            id: 'pillsStyle',
            type: 'select',
            label: 'Text options style',
            group: 'General',
            default: 'pills',
            options: [
                { value: 'pills', label: 'Pills (compact)' },
                { value: 'buttons', label: 'Buttons (equal width, fill row)' },
            ],
        },
        {
            id: 'skipUnavailable',
            type: 'checkbox',
            label: 'Hide & automatically skip sold out variants',
            group: 'General',
            default: false,
        },
        // ── Labels ──────────────────────────────────────────────────────────────
        {
            // BACK-COMPAT label style. 'value' (default) = the exact current behaviour (the selected value
            // alone, e.g. "Sand"); 'option-value' = "Option — value" (e.g. "Color - Sand"), the top-tier
            // Shopify PDP anatomy. Live-updates on selection exactly like the current label (both SSR and the
            // hydrated client render the SAME React output derived from the shared variant state).
            id: 'labelStyle',
            type: 'select',
            label: 'Selected label style',
            group: 'Labels',
            default: 'value',
            options: [
                { value: 'value', label: 'Value only (Sand)' },
                { value: 'option-value', label: 'Option + value (Color - Sand)' },
            ],
        },
        {
            id: 'customLabels',
            type: 'text',
            // 0059 — a Shrine CSV with [name]/[selected] tokens (see resolveLabel), not a sentence.
            aiWritable: false,
            label: 'Custom labels',
            group: 'Labels',
            default: '',
            info: 'Dynamic keywords: [name] = the option name, [selected] = the chosen value. Example: Shoe size [selected]',
        },
        {
            id: 'labelsSize',
            type: 'range',
            label: 'Labels size',
            group: 'Labels',
            min: 10,
            max: 24,
            step: 1,
            default: 15,
            unit: 'px',
        },
        {
            id: 'labelsFont',
            type: 'select',
            label: 'Labels font',
            group: 'Labels',
            default: 'body-bold',
            options: [
                { value: 'body', label: 'Body' },
                { value: 'body-bold', label: 'Body bold' },
                { value: 'headings', label: 'Headings' },
            ],
        },
        {
            id: 'labelsColor',
            type: 'select',
            label: 'Labels color',
            group: 'Labels',
            default: 'text',
            options: [
                { value: 'text', label: 'Text' },
                { value: 'accent-1', label: 'Accent' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'labelsCustomColor',
            type: 'color',
            label: 'Labels custom color',
            group: 'Labels',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'labelsColor', equals: 'custom' },
        },
        {
            // Shrine `show_label_lines` — replaces the plain label with a centred label flanked by side rules.
            id: 'showLabelLines',
            type: 'checkbox',
            label: 'Show lines next to labels',
            group: 'Labels',
            default: false,
        },
        {
            // Shrine `labels_alignment` (only meaningful when label lines are OFF).
            id: 'labelsAlignment',
            type: 'select',
            label: 'Labels alignment',
            group: 'Labels',
            default: 'flex-start',
            options: [
                { value: 'flex-start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Right' },
            ],
            visibleWhen: { field: 'showLabelLines', equals: false },
        },
        {
            // Shrine `labels_line_color`.
            id: 'labelsLineColor',
            type: 'select',
            label: 'Lines color',
            group: 'Labels',
            default: 'text',
            options: [
                { value: 'text', label: 'Text' },
                { value: 'accent-1', label: 'Accent' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'custom', label: 'Custom' },
            ],
            visibleWhen: { field: 'showLabelLines', equals: true },
        },
        {
            // Shrine `labels_line_custom_color`.
            id: 'labelsLineCustomColor',
            type: 'color',
            label: 'Lines custom color',
            group: 'Labels',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'labelsLineColor', equals: 'custom' },
        },
        {
            // Shrine `labels_line_thickness`.
            id: 'labelsLineThickness',
            type: 'range',
            label: 'Lines thickness',
            group: 'Labels',
            min: 1,
            max: 6,
            step: 1,
            default: 2,
            unit: 'px',
            visibleWhen: { field: 'showLabelLines', equals: true },
        },
        // ── Color swatches ──────────────────────────────────────────────────────
        {
            id: 'swatchesSize',
            type: 'select',
            label: 'Swatches size',
            group: 'Color swatches',
            default: 'medium',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
                { value: 'extra-large', label: 'Extra large' },
            ],
        },
        {
            // Shrine `swatches_custom_colors` — colour source. We resolve `custom` (CSS list) + `disabled`;
            // `image_alt`/`predefined`/`metafield` are Shopify-sourced and fall back to the variant swatch.
            id: 'swatchesCustomColors',
            type: 'select',
            label: 'Swatch color source',
            group: 'Color swatches',
            default: 'disabled',
            options: [
                { value: 'disabled', label: 'Disabled (variant swatch)' },
                { value: 'image_alt', label: 'Variant image alt text' },
                { value: 'custom', label: 'Custom colors list' },
                { value: 'predefined', label: 'Theme color swatches' },
                { value: 'metafield', label: 'Category metafields' },
            ],
        },
        {
            // Shrine `swatches_custom_colors_list` — comma-split CSS hex/gradient, one per variant.
            id: 'swatchesCustomColorsList',
            aiWritable: false, // 0059 — machine format, not prose
            type: 'text',
            label: 'Custom colors',
            group: 'Color swatches',
            default: '#000000, #FFFFFF',
            info: 'Comma-separated CSS colors/gradients, one per variant.',
            visibleWhen: { field: 'swatchesCustomColors', equals: 'custom' },
        },
        // ── Dropdowns ───────────────────────────────────────────────────────────
        {
            // Shrine `full_width_dropdowns`.
            id: 'fullWidthDropdowns',
            type: 'checkbox',
            label: 'Full width dropdowns',
            group: 'Dropdowns',
            default: false,
        },
        // ── Step indicator ──────────────────────────────────────────────────────
        {
            id: 'showStepIndicator',
            type: 'checkbox',
            label: 'Show step indicator',
            group: 'Step indicator',
            default: false,
            info: 'Numbered badge before each label (e.g. "1 Select your scent").',
        },
        {
            id: 'stepIndicatorSize',
            type: 'range',
            label: 'Size',
            group: 'Step indicator',
            min: 14,
            max: 40,
            step: 1,
            default: 22,
            unit: 'px',
            visibleWhen: { field: 'showStepIndicator', equals: true },
        },
        {
            // Shrine `step_indicator_bg_color`.
            id: 'stepIndicatorBgColor',
            type: 'color',
            label: 'Background color',
            group: 'Step indicator',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'showStepIndicator', equals: true },
        },
        {
            // Shrine `step_indicator_text_color`.
            id: 'stepIndicatorTextColor',
            type: 'color',
            label: 'Text color',
            group: 'Step indicator',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'showStepIndicator', equals: true },
        },
        {
            // Shrine `step_indicator_corner_radius` (% — 100 = circle, 0 = square).
            id: 'stepIndicatorCornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Step indicator',
            min: 0,
            max: 100,
            step: 5,
            default: 60,
            unit: '%',
            visibleWhen: { field: 'showStepIndicator', equals: true },
        },
        // ── Margins ─────────────────────────────────────────────────────────────
        ...marginFields(15, 15),
    ],
};
const selectStyle = {
    inlineSize: '100%',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    fontSize: 'var(--aqx-theme-text-base, 16px)',
    paddingBlock: 'var(--aqx-theme-space-3, 12px)',
    paddingInline: 'var(--aqx-theme-space-4, 16px)',
    borderRadius: 'var(--aqx-theme-radius-md, 10px)',
    border: '1px solid var(--aqx-scheme-border)',
    background: 'var(--aqx-scheme-bg)',
    color: 'var(--aqx-scheme-text)',
    cursor: 'pointer',
};
/** Shrine swatch dimensions (px) — small / medium / large / extra-large. */
const SWATCH_PX = { small: 36, medium: 44, large: 56, 'extra-large': 68 };
/**
 * A PHOTO swatch is a BARE PHOTOGRAPH, not a dot and not a boxed thumbnail (founder call, 14/09, after
 * seeing the first shipped row: the 60 px tiles on a grey ground read as "des boîtes grises" — little
 * grey boxes with a smear inside, not as a row of shoes). A 44 px circle crops a product shot into an
 * unreadable smear; a 60 px tile on `--aqx-scheme-surface` turns the row into a grid of containers.
 *
 * The anatomy we ship instead is the reference one (on.com, Norda): NO ground, NO corner radius, NO
 * frame — the photograph alone, big enough to recognise the item, and the chosen one marked by a rule
 * UNDER the photo rather than a ring around it. Sized off the SAME `swatchesSize` control so the seller
 * keeps one dial, but scaled up: the sizes that read as a colour dot are far too small for a photograph.
 */
const PHOTO_TILE_PX = {
    small: 56,
    medium: 80,
    large: 96,
    'extra-large': 112,
};
/**
 * The lane reserved UNDER each photo for the selection rule. Reserved on every tile, selected or not,
 * so choosing a colour never nudges the row — and the photo keeps a real gap above the rule instead of
 * having it drawn across its own bottom edge.
 *
 * The photo is BOTTOM-aligned inside its box (see `backgroundPosition`), not centred: a `contain` fit
 * leaves slack wherever the source aspect isn't square, and a centred photo pushed the rule ~20 px
 * below the shoe — it read as a stray bar under the row instead of as an underline (measured on the
 * 4:3 shot, 14/09). Bottom-aligned, the item sits on the rule whatever its aspect ratio.
 */
const PHOTO_UNDERLINE_LANE_PX = 8;
// The canonical `StorefrontVariant.label` join ("Sky Blue / Standard") — one value per option — is
// `OPTION_SEP`, imported above: shared with the per-unit rows of the offer tiles (28/09/2026), so both
// selectors read a label the same way.
/** Map the Labels color select to a CSS color (custom resolves the ColorValue hex). */
function labelColor(value, custom) {
    switch (value) {
        case 'accent-1':
        case 'accent-2':
            return 'var(--aqx-scheme-accent)';
        case 'custom':
            return resolveHex(custom, '#dd1d1d');
        case 'text':
        default:
            return 'var(--aqx-scheme-text)';
    }
}
/** Map the Labels font select to a CSS font-family/weight pair. */
function labelFont(value) {
    switch (value) {
        case 'headings':
            return {
                fontFamily: 'var(--aqx-theme-heading-font, var(--aqx-theme-font))',
                fontWeight: 600,
            };
        case 'body':
            return { fontFamily: 'inherit', fontWeight: 400 };
        case 'body-bold':
        default:
            return { fontFamily: 'inherit', fontWeight: 600 };
    }
}
/**
 * Resolve the option label, honouring Shrine `custom_labels` tokens. Shrine splits the CSV per option;
 * with our single-option demo we apply the first entry. [name] → the PRODUCT OPTION's name ("Taille"),
 * [selected] → the current variant's label ("39") — so `pointure [selected]` renders « pointure 39 » and
 * `[name] [selected]` renders « Taille 39 ». `fallback` is what an EMPTY CSV keeps (the historical label),
 * kept separate from the [name] token so writing a custom label never changes the default render.
 */
function resolveLabel(customLabels, fallback, optionName, selectedLabel) {
    const first = customLabels.split(',')[0]?.trim() ?? '';
    if (!first)
        return fallback;
    return first.replace(/\[name\]/gi, optionName).replace(/\[selected\]/gi, selectedLabel);
}
/** Variant selector — swatch / pills / dropdown (Shopify-style), bound to the shared variant state. */
const VariantPickerBlock = ({ block, context }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    return (_jsx(VariantPicker, { bs: block.settings, ps: ps, blockId: block.id, context: context }));
};
function VariantPicker({ bs, ps, blockId, context, }) {
    const { variants: allVariants, variantId, setVariantId } = ps;
    /**
     * L'INTENTION DE VOIR — survol, focus clavier, ou tout premier contact du doigt sur une pastille.
     *
     * La galerie ne peut peindre une couleur que si son fichier est arrivé, et il ne part qu'au clic :
     * mesuré le 19/09/2026 sur la préprod, 240 à 280 ms s'écoulent entre le clic et la photo peinte, sur
     * une fibre et sans bridage. Ce geste-ci arrive AVANT le clic (un survol dure une demi-seconde, un
     * `pointerdown` précède son `click` de plusieurs dizaines de millisecondes), donc la demande part
     * pendant que la main est encore en route et le clic ne trouve plus rien à attendre.
     *
     * La galerie est dans une AUTRE racine React (colonne média vs colonne achat) : on passe donc par le
     * registre de `imageWarm`, comme la sélection de variante passe déjà par son propre magasin.
     */
    const intentToSee = (optionValueLabel) => {
        warmVariantImages(productWarmKey(ps.product), optionValueLabel);
    };
    // Shrine `skip_unavailable`: hide sold-out variants from the picker entirely.
    const skipUnavailable = bs.skipUnavailable === true;
    const variants = skipUnavailable ? allVariants.filter((v) => v.available) : allVariants;
    if (variants.length === 0)
        return null;
    // A SIMPLE product (no options) carries ONE default, UNLABELLED variant — the backend strips Shopify's
    // "Default Title" sentinel on import, and `pickerVariants` keeps that lone variant in the list ONLY so
    // downstream blocks (price / sku / cod-form / sticky-atc) read its REAL backend UUID. There is nothing
    // to choose, so the picker hides itself (Shopify behaviour). Without this the storefront rendered an
    // empty label above a blank full-width pill, while the builder — whose product context derives variants
    // from the OPTIONS (`variantsFromOptions` ⇒ empty for a simple product) — correctly showed nothing.
    if (variants.length === 1 && (variants[0]?.label ?? '').trim() === '')
        return null;
    const cls = `aqx-vp-${blockId}`;
    const vis = visibilityCss(cls, bs.visibility);
    const style = str(bs.variantStyle, 'auto');
    const useDropdown = style === 'dropdown';
    const useSwatches = !useDropdown &&
        (style === 'swatch' || (style !== 'pills' && variants.some((v) => v.swatch || v.photo)));
    const current = variants.find((v) => v.id === variantId) ?? variants[0];
    const currentIndex = Math.max(0, variants.findIndex((v) => v.id === (current?.id ?? '')));
    const labelsSize = num(bs.labelsSize, 15);
    const labelStyleColor = labelColor(bs.labelsColor, bs.labelsCustomColor);
    const { fontFamily: labelFamily, fontWeight: labelWeight } = labelFont(bs.labelsFont);
    const swatchPx = SWATCH_PX[str(bs.swatchesSize, 'medium')] ?? 44;
    const photoPx = PHOTO_TILE_PX[str(bs.swatchesSize, 'medium')] ?? 60;
    // Shrine label lines: a centred label flanked by horizontal rules; overrides `labels_alignment`.
    const showLabelLines = bs.showLabelLines === true;
    const lineColor = labelColor(bs.labelsLineColor, bs.labelsLineCustomColor);
    const lineThickness = num(bs.labelsLineThickness, 2);
    const labelsAlignment = str(bs.labelsAlignment, 'flex-start');
    // Shrine custom swatch colours: `custom` resolves the comma-split CSS list per variant index;
    // Shopify-sourced sources (image_alt/predefined/metafield) fall back to the variant's own swatch.
    const swatchSource = str(bs.swatchesCustomColors, 'disabled');
    const customSwatchColors = swatchSource === 'custom'
        ? str(bs.swatchesCustomColorsList)
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean)
        : [];
    const swatchColorFor = (v, i) => customSwatchColors.length > 0
        ? (customSwatchColors[i] ?? v.swatch ?? undefined)
        : (v.swatch ?? undefined);
    const fullWidthDropdowns = bs.fullWidthDropdowns === true;
    const showStep = bs.showStepIndicator === true;
    const stepSize = num(bs.stepIndicatorSize, 22);
    const stepBg = resolveHex(bs.stepIndicatorBgColor, '#000000');
    const stepText = resolveHex(bs.stepIndicatorTextColor, '#ffffff');
    const stepRadius = num(bs.stepIndicatorCornerRadius, 60);
    const optionName = current?.label ?? 'Variante';
    // `labelStyle` (back-compat, default 'value' = the historical label): 'option-value' prefixes the REAL
    // option name from the product's first option ("Color - Sand"). `customLabels`, when set, always wins
    // (its [name]/[selected] tokens keep their existing resolution). The label derives from the shared
    // variant state, so it live-updates on selection in BOTH paths (SSR initial render + client re-render).
    const realOptionName = ps.product.options?.[0]?.name ?? '';
    const customLabels = str(bs.customLabels);
    const optionValueStyle = str(bs.labelStyle, 'value') === 'option-value';
    const labelText = !customLabels.trim() && optionValueStyle && realOptionName
        ? `${realOptionName} - ${current?.label ?? ''}`
        : // [name] is the PRODUCT OPTION's name ("Taille"); products without options keep the variant label.
            resolveLabel(customLabels, optionName, realOptionName || optionName, current?.label ?? '');
    // ── Shared row primitives (flat single-option render AND multi-option rows) ─────────────────────
    // Extracted as closures so both paths emit byte-identical markup for the same inputs — the flat path
    // below must stay EXACTLY the historical render (a large test suite depends on it).
    const dropdownSizing = fullWidthDropdowns
        ? selectStyle
        : { ...selectStyle, inlineSize: 'auto', minInlineSize: '12rem' };
    /** The label line: optional side rules (Shrine label lines) + optional step badge + the label text. */
    const labelRow = (stepNumber, text) => (_jsxs("div", { style: {
            display: 'flex',
            alignItems: 'center',
            // Shrine: label lines force a centred label; otherwise honour `labels_alignment`.
            justifyContent: showLabelLines ? 'center' : labelsAlignment,
            gap: 'var(--aqx-theme-space-2, 8px)',
            marginBottom: 'var(--aqx-theme-space-2, 8px)',
        }, children: [showLabelLines ? (_jsx("span", { "aria-hidden": "true", style: {
                    flex: 1,
                    blockSize: 0,
                    borderTop: `${lineThickness}px solid ${lineColor}`,
                } })) : null, showStep ? (_jsx("span", { "aria-hidden": "true", style: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    inlineSize: `${stepSize}px`,
                    blockSize: `${stepSize}px`,
                    flexShrink: 0,
                    // Shrine `step_indicator_corner_radius` is a % (100 = circle, 0 = square).
                    borderRadius: `${stepRadius / 2}%`,
                    background: stepBg,
                    color: stepText,
                    fontSize: `${Math.round(stepSize * 0.55)}px`,
                    fontWeight: 600,
                    lineHeight: 1,
                }, children: stepNumber })) : null, _jsx("span", { style: {
                    fontSize: `${labelsSize}px`,
                    fontFamily: labelFamily,
                    fontWeight: labelWeight,
                    color: labelStyleColor,
                }, children: text }), showLabelLines ? (_jsx("span", { "aria-hidden": "true", style: {
                    flex: 1,
                    blockSize: 0,
                    borderTop: `${lineThickness}px solid ${lineColor}`,
                } })) : null] }));
    /**
     * One swatch radio. `hardDisable` is the flat path's historical sold-out treatment (a real `disabled`
     * + not-allowed cursor). Multi-option rows pass `false`: a sold-out COMBINATION renders greyed (same
     * opacity) with `aria-disabled` but stays CLICKABLE — a colour merely out of stock in the CURRENT size
     * must remain reachable (selecting it surfaces the sold-out variant downstream, Shopify-style).
     */
    const swatchButton = (opts) => (_jsx("button", { type: "button", role: "radio", "aria-checked": opts.selected, "aria-label": opts.label, title: opts.label, disabled: opts.hardDisable && !opts.available, "aria-disabled": !opts.hardDisable && !opts.available ? true : undefined, onPointerEnter: opts.onIntent, onPointerDown: opts.onIntent, onFocus: opts.onIntent, onClick: opts.onClick, style: opts.photo
            ? {
                // BARE PHOTOGRAPH. `contain`, never `cover`: a seller's product shot is the whole item on
                // a backdrop, and cropping it to fill the box shows a patch of that backdrop instead of
                // the shoe. The reference rows work precisely because the item is entire.
                boxSizing: 'border-box',
                // At most the chosen size, and NEVER wider than a quarter of the row — so four colours
                // always share one line on a phone. Enlarging the photo to 80 px (14/09) pushed the 4th
                // colour onto a line of its own at 390 px, which reads as an orphan; the cap fixes that
                // without touching wide screens, where a quarter of the row is far more than 80 px and a
                // 13-colour product still flows as many per line as fit.
                inlineSize: `min(${photoPx}px, calc((100% - 3 * var(--aqx-theme-space-2, 8px)) / 4))`,
                // Ratio, not a fixed height: when the cap shrinks the photo the box shrinks with it, so
                // the shoe keeps the same framing and the rule stays right under it at every width.
                aspectRatio: `${photoPx} / ${photoPx + PHOTO_UNDERLINE_LANE_PX}`,
                flexShrink: 0,
                // No ground, no radius, no frame — the three things that made the first row read as grey
                // boxes. `background-origin: content-box` keeps the photo out of the underline lane, so
                // the rule sits BELOW the shoe with a real gap instead of across its bottom edge.
                background: 'transparent',
                // Une pastille fait 56 à 112 px : lui servir le rendu stocké de 2560px, c'est ~317 Ko
                // pour une vignette. Un fond CSS ne sait pas lire un `srcset` — on choisit donc UNE
                // largeur, celle d'un écran 3x, et le serveur l'arrondit à un palier existant. Sans ça,
                // depuis que la galerie demande des variantes, la photo voyagerait DEUX fois : une fois
                // en variante pour le `<img>`, une fois en pleine taille pour ce fond.
                backgroundImage: `url(${JSON.stringify(assetVariantUrl(opts.photo, snapVariantWidth(photoPx * 3)))})`,
                backgroundOrigin: 'content-box',
                backgroundSize: 'contain',
                backgroundPosition: 'center bottom',
                backgroundRepeat: 'no-repeat',
                borderRadius: 0,
                border: 'none',
                paddingInline: 0,
                paddingBlockStart: 0,
                paddingBlockEnd: `${PHOTO_UNDERLINE_LANE_PX}px`,
                cursor: opts.hardDisable && !opts.available ? 'not-allowed' : 'pointer',
                opacity: opts.available ? 1 : 0.4,
                // Selected = a rule UNDER the photo, drawn in the reserved lane (inset, so it never grows
                // the tile and never shifts the row). Unselected wears nothing at all: the references let
                // the photographs breathe and mark the chosen one alone.
                boxShadow: opts.selected ? 'inset 0 -2px 0 0 var(--aqx-scheme-text)' : 'none',
            }
            : {
                inlineSize: `${swatchPx}px`,
                blockSize: `${swatchPx}px`,
                borderRadius: '50%',
                background: opts.bg,
                cursor: opts.hardDisable && !opts.available ? 'not-allowed' : 'pointer',
                opacity: opts.available ? 1 : 0.4,
                // Shrine swatch selected = double ring (bg gap + accent ring).
                boxShadow: opts.selected
                    ? '0 0 0 2px var(--aqx-scheme-bg), 0 0 0 4px var(--aqx-scheme-accent)'
                    : 'none',
                border: '1px solid var(--aqx-scheme-border)',
            } }, opts.key));
    // `pillsStyle: 'buttons'` — the segmented row: every text value an equal-width button that fills
    // the row; selected = accent border + a light accent tint (NOT the filled pill), the reference
    // "Standard | Mini" size-row anatomy. 'pills' keeps the historical chip markup byte-identical.
    const buttonsStyle = str(bs.pillsStyle, 'pills') === 'buttons';
    /** One pill radio — same `hardDisable` contract as {@link swatchButton}. */
    const pillButton = (opts) => (_jsx("button", { type: "button", role: "radio", "aria-checked": opts.selected, disabled: opts.hardDisable && !opts.available, "aria-disabled": !opts.hardDisable && !opts.available ? true : undefined, onPointerEnter: opts.onIntent, onPointerDown: opts.onIntent, onFocus: opts.onIntent, onClick: opts.onClick, style: {
            cursor: opts.hardDisable && !opts.available ? 'not-allowed' : 'pointer',
            opacity: opts.available ? 1 : 0.5,
            paddingBlock: buttonsStyle
                ? 'var(--aqx-theme-space-3, 12px)'
                : 'var(--aqx-theme-space-2, 8px)',
            paddingInline: 'var(--aqx-theme-space-4, 16px)',
            borderRadius: buttonsStyle
                ? 'var(--aqx-theme-radius-md, 10px)'
                : 'var(--aqx-theme-radius-pill, 999px)',
            fontSize: 'var(--aqx-theme-text-sm, 14px)',
            fontFamily: 'inherit',
            ...(buttonsStyle
                ? {
                    // Equal-width buttons that share the row; centred label, accent-tinted when selected.
                    flex: '1 1 0',
                    textAlign: 'center',
                    fontWeight: opts.selected ? 600 : 400,
                    background: opts.selected
                        ? 'color-mix(in srgb, var(--aqx-scheme-accent) 8%, transparent)'
                        : 'transparent',
                    color: opts.selected ? 'var(--aqx-scheme-accent)' : 'var(--aqx-scheme-text)',
                    border: `1px solid ${opts.selected ? 'var(--aqx-scheme-accent)' : 'var(--aqx-scheme-border)'}`,
                }
                : {
                    background: opts.selected ? 'var(--aqx-scheme-accent)' : 'transparent',
                    color: opts.selected
                        ? 'var(--aqx-theme-accent-contrast, #fff)'
                        : 'var(--aqx-scheme-text)',
                    border: `1px solid ${opts.selected ? 'var(--aqx-scheme-accent)' : 'var(--aqx-scheme-border)'}`,
                }),
        }, children: opts.label }, opts.key));
    // ── Multi-option mode (2+ options, e.g. Color × Size) ───────────────────────────────────────────
    // Enter it only when the product carries 2+ options AND every picker-visible variant label parses as
    // the canonical join of one KNOWN value per option ("Sky Blue / Standard"). Anything else — single
    // option, the synthetic first-option-only fallback (`variantsFromOptions`), odd labels — keeps the
    // EXACT flat render below. SSR-safe: the per-row selections DERIVE from the current variant's label
    // (no extra state, no effects), so server and hydrated client render the same tree and the initial
    // render is the first (available, under skip) combination — exactly like the flat path.
    const productOptions = ps.product.options ?? [];
    const multiOption = productOptions.length >= 2 &&
        variants.every((v) => {
            const parts = v.label.split(OPTION_SEP);
            return (parts.length === productOptions.length &&
                parts.every((p, i) => productOptions[i]?.values.some((val) => val.label === p) === true));
        });
    if (multiOption && current) {
        const currentParts = current.label.split(OPTION_SEP);
        /** The variant matching one value per option (labels joined with the canonical " / "). */
        const findCombo = (parts) => variants.find((v) => v.label === parts.join(OPTION_SEP));
        /** The combination = the current selections with row `rowIdx` swapped to `valueLabel`. */
        const comboFor = (rowIdx, valueLabel) => {
            const parts = [...currentParts];
            parts[rowIdx] = valueLabel;
            return findCombo(parts);
        };
        const selectValue = (rowIdx, valueLabel) => {
            const exact = comboFor(rowIdx, valueLabel);
            if (exact) {
                // May be sold out — still selectable; downstream (price/buy-box) surfaces the unavailable state.
                setVariantId(exact.id);
                return;
            }
            // The combination doesn't exist in the matrix → jump to the nearest variant carrying this value
            // (prefer an available one); the OTHER rows adjust, so every listed value stays reachable.
            const carrying = variants.filter((v) => v.label.split(OPTION_SEP)[rowIdx] === valueLabel);
            const target = carrying.find((v) => v.available) ?? carrying[0];
            if (target)
                setVariantId(target.id);
        };
        // `custom_labels` multi-option: Shrine's CSV carries ONE ENTRY PER OPTION — entry i labels row i
        // ([name] → the option's name, [selected] → the row's selected VALUE, live-updating). A missing or
        // empty entry falls back to the `labelStyle` default ('option-value' → "Color - Sky Blue",
        // 'value' → "Sky Blue"). Single-option keeps the historical first-entry behaviour above, untouched.
        const rowLabelText = (rowIdx, name, selectedValue) => {
            const entry = customLabels.split(',')[rowIdx]?.trim() ?? '';
            if (entry) {
                return entry.replace(/\[name\]/gi, name).replace(/\[selected\]/gi, selectedValue);
            }
            return optionValueStyle ? `${name} - ${selectedValue}` : selectedValue;
        };
        return (_jsxs(_Fragment, { children: [vis ? _jsx("style", { dangerouslySetInnerHTML: { __html: vis } }) : null, _jsx("div", { className: cls, style: {
                        ...marginStyle(bs),
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--aqx-theme-space-4, 16px)',
                    }, children: productOptions.map((option, rowIdx) => {
                        const selectedValue = currentParts[rowIdx] ?? '';
                        // Shrine `skip_unavailable` per row: hide a value only when NO remaining (available)
                        // variant carries it at all — partial availability is the per-combination grey below.
                        const values = skipUnavailable
                            ? option.values.filter((val) => variants.some((v) => v.label.split(OPTION_SEP)[rowIdx] === val.label))
                            : option.values;
                        // Per-row style resolution: the single `variantStyle` is the DEFAULT for every row;
                        // 'auto' = swatch row when THIS option's values carry swatch data, else pills. A forced
                        // 'swatch' still falls back per VALUE to a pill when no colour resolves (flat-path idiom).
                        // 'auto' turns a row into swatches when its values carry EITHER a colour or a photo — a
                        // seller who uploaded photos but never picked hex codes gets photo swatches, not text pills.
                        const rowSwatches = !useDropdown &&
                            (style === 'swatch' ||
                                (style !== 'pills' && option.values.some((val) => val.swatch || val.imageUrl)));
                        return (_jsxs("div", { children: [labelRow(rowIdx + 1, rowLabelText(rowIdx, option.name, selectedValue)), useDropdown ? (_jsx("select", { "aria-label": option.name, value: selectedValue, onChange: (e) => selectValue(rowIdx, e.target.value), style: dropdownSizing, children: values.map((val) => (
                                    // Sold-out combos keep the flat path's " — épuisé" suffix but stay selectable
                                    // (no `disabled`): a native-disabled option would dead-end a value that is only
                                    // out of stock against the OTHER rows' current selections.
                                    _jsxs("option", { value: val.label, children: [val.label, comboFor(rowIdx, val.label)?.available === true
                                                ? ''
                                                : t(context, 'product.sold_out_suffix')] }, val.label))) })) : (_jsx("div", { role: "radiogroup", "aria-label": option.name, style: {
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 'var(--aqx-theme-space-2, 8px)',
                                    }, children: values.map((val, valueIdx) => {
                                        // Availability = THIS value combined with the other rows' CURRENT selections.
                                        const available = comboFor(rowIdx, val.label)?.available === true;
                                        const selected = val.label === selectedValue;
                                        // The custom colors list indexes each row's values from its own start (the sane
                                        // multi-row reading of Shrine's flat one-per-variant list).
                                        const bg = rowSwatches
                                            ? customSwatchColors.length > 0
                                                ? (customSwatchColors[valueIdx] ?? val.swatch ?? undefined)
                                                : (val.swatch ?? undefined)
                                            : undefined;
                                        // A photographed value renders as a swatch even with no hex behind it — the photo
                                        // IS the colour. `bg` then falls back to the scheme border so the circle has an
                                        // edge while the image loads.
                                        const photo = rowSwatches ? (val.imageUrl ?? undefined) : undefined;
                                        const onClick = () => selectValue(rowIdx, val.label);
                                        const onIntent = () => intentToSee(val.label);
                                        return bg || photo
                                            ? swatchButton({
                                                key: val.label,
                                                label: val.label,
                                                bg: bg ?? 'var(--aqx-scheme-border)',
                                                photo,
                                                selected,
                                                available,
                                                hardDisable: false,
                                                onClick,
                                                onIntent,
                                            })
                                            : pillButton({
                                                key: val.label,
                                                label: val.label,
                                                selected,
                                                available,
                                                hardDisable: false,
                                                onClick,
                                                onIntent,
                                            });
                                    }) }))] }, option.name));
                    }) })] }));
    }
    return (_jsxs(_Fragment, { children: [vis ? _jsx("style", { dangerouslySetInnerHTML: { __html: vis } }) : null, _jsxs("div", { className: cls, style: marginStyle(bs), children: [labelRow(currentIndex + 1, labelText), useDropdown ? (_jsx("select", { "aria-label": optionName, value: current?.id ?? '', onChange: (e) => setVariantId(e.target.value), 
                        // Shrine `full_width_dropdowns`: stretch to the container, else size to content.
                        style: dropdownSizing, children: variants.map((v) => (_jsxs("option", { value: v.id, disabled: !v.available, children: [v.label, v.available ? '' : t(context, 'product.sold_out_suffix')] }, v.id))) })) : (_jsx("div", { role: "radiogroup", "aria-label": optionName, style: { display: 'flex', flexWrap: 'wrap', gap: 'var(--aqx-theme-space-2, 8px)' }, children: variants.map((v, i) => {
                            const selected = v.id === (current?.id ?? '');
                            const swatchBg = swatchColorFor(v, i);
                            const photo = v.photo ?? undefined;
                            const common = {
                                key: v.id,
                                label: v.label,
                                selected,
                                available: v.available,
                                hardDisable: true,
                                onClick: () => setVariantId(v.id),
                                onIntent: () => intentToSee(v.label),
                            };
                            return useSwatches && (swatchBg || photo)
                                ? swatchButton({ ...common, bg: swatchBg ?? 'var(--aqx-scheme-border)', photo })
                                : pillButton(common);
                        }) }))] })] }));
}
function resolveHex(v, fallback) {
    if (v && typeof v === 'object' && 'value' in v) {
        const val = v.value;
        if (typeof val === 'string')
            return val;
    }
    if (typeof v === 'string')
        return v;
    return fallback;
}
export const variantPickerBlock = {
    type: 'variant-picker',
    schema: variantPickerBlockSchema,
    Component: VariantPickerBlock,
    meta: { label: 'Variant picker' },
};
//# sourceMappingURL=index.js.map